"use client"

import * as React from "react"
import { type DateRange } from "react-day-picker"
import { Calendar as CalendarIcon } from "lucide-react"
import { Calendar } from "@/components/ui/calendar"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const formatDateRange = (dateRange) => {
  if (!dateRange) return null
  const { from, to } = dateRange
  if (!from) return null
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  
  if (!to) return formatDate(from)
  return `${formatDate(from)} - ${formatDate(to)}`
}

export function CalendarRangePicker({ 
  value, 
  onChange, 
  placeholder = "Filter by date",
  className 
}) {
  const [open, setOpen] = React.useState(false)
  const [dateRange, setDateRange] = React.useState(value)

  React.useEffect(() => {
    setDateRange(value)
  }, [value])

  const handleSelect = (range) => {
    setDateRange(range)
  }

  const handleConfirm = () => {
    onChange?.(dateRange)
    setOpen(false)
  }

  const handleClear = () => {
    setDateRange(undefined)
    onChange?.(null)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal bg-[#111318] border-white/10 text-zinc-300 hover:bg-[#111318] hover:text-zinc-100",
            !value && "text-zinc-500",
            className
          )}
        >
          <CalendarIcon size={14} className="mr-2 text-zinc-500" />
          {value ? formatDateRange(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0 bg-[#0a0a0a] border-white/10" 
        align="start"
      >
        <div className="p-4">
          <Calendar
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            className="rounded-lg"
          />
          <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-white/10">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-zinc-400 hover:text-zinc-100"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
